'use client';

import { useGlobalState } from '@/layers/presentation/context/GlobalStateContext';
import { useThrowNotification } from '@/layers/presentation/hooks';
import { useTranslation } from '@/layers/presentation/i18n/client';
import { NotificationType, UserType } from '@/types/modelEnums';
import { Button } from '@nextui-org/react';
import { Bookmark, BookmarkCheck, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface SaveProgramButtonProps {
  programId: number;
  variant?: 'solid' | 'bordered' | 'light' | 'flat' | 'faded' | 'shadow' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SaveProgramButton: React.FC<SaveProgramButtonProps> = ({
  programId,
  variant = 'bordered',
  size = 'sm',
  className = '',
}) => {
  const { t } = useTranslation();
  const { user } = useGlobalState();
  const throwNotification = useThrowNotification();
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Only check if user is a provider or patient
    if (user?.type !== UserType.PROVIDER && user?.type !== UserType.PATIENT) {
      return;
    }

    // Check if program is already saved
    const checkSavedStatus = async () => {
      try {
        const response = await fetch(`/api/savedPrograms/check/${programId}`);
        if (response.ok) {
          const data = await response.json();
          setIsSaved(data.isSaved);
        }
      } catch (error) {
        console.error('Error checking saved status:', error);
      }
    };

    checkSavedStatus();
  }, [programId, user?.type]);

  const handleToggleSave = async () => {
    setIsLoading(true);

    try {
      if (isSaved) {
        // Unsave
        const response = await fetch(`/api/savedPrograms/${programId}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          setIsSaved(false);
          throwNotification(NotificationType.Success, t('components:programCard.unsaveSuccess'));
        } else {
          const data = await response.json();
          throwNotification(
            NotificationType.Error,
            data.error || t('components:programCard.saveError'),
          );
        }
      } else {
        // Save
        const response = await fetch('/api/savedPrograms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ programId }),
        });

        if (response.ok) {
          setIsSaved(true);
          throwNotification(NotificationType.Success, t('components:programCard.saveSuccess'));
        } else if (response.status === 409) {
          setIsSaved(true);
          throwNotification(NotificationType.Info, t('components:programCard.alreadySaved'));
        } else {
          const data = await response.json();
          throwNotification(
            NotificationType.Error,
            data.error || t('components:programCard.saveError'),
          );
        }
      }
    } catch (error) {
      console.error('Error toggling save:', error);
      throwNotification(NotificationType.Error, t('components:programCard.saveError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Only show for providers and patients
  if (user?.type !== UserType.PROVIDER && user?.type !== UserType.PATIENT) {
    return null;
  }

  return (
    <Button
      variant={variant}
      size={size}
      color={isSaved ? 'primary' : 'default'}
      startContent={
        isLoading ? (
          <Loader2 size={18} className="animate-spin" />
        ) : isSaved ? (
          <BookmarkCheck size={18} />
        ) : (
          <Bookmark size={18} />
        )
      }
      onPress={handleToggleSave}
      isDisabled={isLoading}
      className={className}
    >
      {isSaved ? t('components:programCard.saved') : t('components:programCard.save')}
    </Button>
  );
};

export default SaveProgramButton;
